import { EHttpMethods } from "../../../../enums";
import { AUTH_BASE_URL } from "../../../constants";
import { getToken } from "../../../context/authContext";
import { ApiServiceManager, fetchRequest } from "../../apiServiceManager";
import {
  LIST_ORGANIZATIONS_URL,
  ORGANISATIONS_ADMIN_URL,
  PAYMENT_TYPES_URL,
  SERVICE_PROVIDER_FILTER_QUERY,
} from "./constants";
import {
  IOrganizationBankInfoResponse,
  IOrganizationDocumentationResponse,
  IOrganizationTierResponse,
  IOrganizationFeeTemplate,
  IOrganizationPaymentFee,
  IAddOrganizationFeeTemplateBody,
  IOrganizationPaymentTypeResponse,
  IPaymentTypesResponse,
  IUpdateTierFeesBody,
  IOrganizationsListResponse,
  IOrganizationDetailResponse,
  INewOrganizationBody,
  IEditOrganizationBody,
  IDeleteOrganizationBody,
  IBaseApiResponse,
  IAddOrganizationDocumentationBody,
  IOrgClientListResponse,
  IOrgClientAuditListResponse,
  IOrgClientCreatedResponse,
  CheckoutLoginMode,
  OrganizationRequestStatus,
  OrganizationRequestType,
  IOrganizationRequestListResponse,
} from "./models";
import { IAddNewUserBody, IClientAdminResponse } from "./types";
import { IApiResponse } from "../../models";
import { TOrganizationVerificationStatus } from "../../../types/Organisation";

// eslint-disable-next-line no-unused-vars
const defaultProvide = async () => await getToken();

export const getAllOrganizations = (
  page = 0,
  size = 10,
  searchValue = "",
  verificationStatus?:
    | TOrganizationVerificationStatus
    | TOrganizationVerificationStatus[],
  serviceProvidersOnly = false
): Promise<IOrganizationsListResponse> => {
  const url = {
    get value() {
      let base = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}?page=${page}&size=${size}&sort=createdAt,DESC`;
      if (searchValue !== "") {
        const encoded = searchValue.replace("+", "%2B");
        base += `&searchKeyword=${encoded}`;
      }
      if (Array.isArray(verificationStatus)) {
        if (verificationStatus.length > 0) {
          base += `&verificationStatuses=${verificationStatus.join(",")}`;
        }
      } else if (verificationStatus) {
        base += `&verificationStatus=${verificationStatus}`;
      }
      if (serviceProvidersOnly) {
        base += `&${SERVICE_PROVIDER_FILTER_QUERY}`;
      }
      return base;
    },
  };

  const descriptor = new ApiServiceManager(
    url.value,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/** Approves a PENDING organization: grants ORGANIZATION_OWNER, pushes KC claims, creates a default fee template. */
export const verifyOrganization = (
  code: string
): Promise<IOrganizationDetailResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/verify`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/** Rejects a PENDING organization with a reason. */
export const rejectOrganization = (
  code: string,
  rejectReason: string
): Promise<IOrganizationDetailResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/reject`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, { rejectReason });
};

export const addNewOrganization = (
  body: INewOrganizationBody
): Promise<IOrganizationDetailResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const addNewOrganizationUser = (
  body: IAddNewUserBody
): Promise<IClientAdminResponse> => {
  const url = `${AUTH_BASE_URL}${ORGANISATIONS_ADMIN_URL}/organization-admin-sign-up`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const editOrganization = (
  code: string,
  body: IEditOrganizationBody
): Promise<IOrganizationDetailResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const deleteOrganizationAPI = (
  code: string,
  body: IDeleteOrganizationBody
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.DELETE,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const getOrganization = (
  code: string
): Promise<IOrganizationDetailResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationPaymentMethods = (
  code: string
): Promise<IOrganizationPaymentTypeResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/paymentTypes`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/** The payment types (gateways) known to the backend, with the database ids that fee records
 * have to reference. */
export const getPaymentTypes = (): Promise<IPaymentTypesResponse> => {
  const url = `${AUTH_BASE_URL}${PAYMENT_TYPES_URL}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationBanks = (
  code: string
): Promise<IOrganizationBankInfoResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/banks`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationDocumentations = (
  code: string
): Promise<IOrganizationDocumentationResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/attachments`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const addOrganizationDocumentation = (
  code: string,
  body: IAddOrganizationDocumentationBody
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/attachments`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const updateOrganizationDocumentation = (
  code: string,
  id: number,
  body: IAddOrganizationDocumentationBody
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/attachments/${id}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PUT,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const deleteOrganizationDocumentation = (
  code: string,
  id: number
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/attachments/${id}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.DELETE,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationTiers = (
  code: string
): Promise<IOrganizationTierResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/fee-templates/active/details`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const addBulkOrganizationPaymentTypeFee = (
  code: string,
  body: IOrganizationPaymentFee[]
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/bulk-payment-types-fee`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const getOrganizationSettings = (
  code: string
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/settings`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const updateOrganizationSettings = (
  code: string,
  body: string[]
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/settings`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PUT,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const addOrganizationFeeTemplate = (
  code: string,
  body: IAddOrganizationFeeTemplateBody
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/fee-templates`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const addOrganizationPaymentTypeFee = (
  code: string,
  body: IOrganizationPaymentFee
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/payment-types-fee`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const updateOrganizationPaymentTypeFees = (
  code: string,
  body: IUpdateTierFeesBody
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/payment-types-fee`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PUT,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const updateFeeTemplate = (
  id: number,
  body: Partial<IOrganizationFeeTemplate>
): Promise<IOrganizationTierResponse> => {
  const url = `${AUTH_BASE_URL}/api/organizations/fee-templates/${id}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PUT,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

/**
 * Every Keycloak client for this org — self-service, staff-added, or migrated (design doc §9
 * Phase 2). The same list an org's own CLIENT_ADMIN/ORGANIZATION_OWNER sees in waffy-portal, plus
 * staff-only enable/disable and consent-flag control here.
 */
export const getOrganizationClients = (
  code: string
): Promise<IOrgClientListResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/clients`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/**
 * Creates a new Keycloak client for this org — same endpoint waffy-portal's self-service "API
 * clients" section uses, `ADMIN`-permitted here for staff (design doc §9 Phase 2). Always
 * auto-accept-consent=false regardless of the org's other clients. The response's clientSecret
 * is shown once, here, and never retrievable again afterward.
 */
export const createOrganizationClient = (
  code: string,
  description?: string
): Promise<IOrgClientCreatedResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/clients`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, description ? { description } : undefined);
};

export const setOrganizationClientEnabled = (
  code: string,
  clientId: string,
  enabled: boolean
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/clients/${clientId}/enabled`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, { enabled });
};

/**
 * Design doc §6.2 — granting or revoking auto-accept-consent is a documented, reviewed
 * exception, never a bare toggle, so `reason` is always required.
 */
export const setOrganizationClientAutoAcceptConsent = (
  code: string,
  clientId: string,
  autoAcceptConsent: boolean,
  reason: string
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/clients/${clientId}/auto-accept-consent`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, { autoAcceptConsent, reason });
};

/**
 * Design doc §6.5 — staff-only, reason-gated, same discipline as auto-accept-consent (§6.2):
 * this is a per-org override of an abuse/cost control, not a routine toggle. `otpSendHourlyLimit`
 * of `null` clears the override, reverting the client to the global default.
 */
export const setOrganizationClientOtpSendHourlyLimit = (
  code: string,
  clientId: string,
  otpSendHourlyLimit: number | null,
  reason: string
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/clients/${clientId}/otp-send-hourly-limit`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, { otpSendHourlyLimit, reason });
};

/**
 * Staff-only, reason-gated - which checkout URL shapes the client allows (design doc §7.3b):
 * "any", "hint" (a ticket or a URL with the customer's phone) or "ticket" (a ticket only).
 */
export const setOrganizationClientCheckoutLoginMode = (
  code: string,
  clientId: string,
  checkoutLoginMode: CheckoutLoginMode,
  reason: string
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/clients/${clientId}/checkout-login-mode`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, { checkoutLoginMode, reason });
};

/**
 * Staff-only, reason-gated - the per-client override of consent/send's hourly limit. `null` clears it,
 * reverting the client to the realm default.
 */
export const setOrganizationClientConsentSendHourlyLimit = (
  code: string,
  clientId: string,
  consentSendHourlyLimit: number | null,
  reason: string
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/clients/${clientId}/consent-send-hourly-limit`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, { consentSendHourlyLimit, reason });
};

export const getOrganizationClientAudit = (
  code: string,
  page = 0,
  size = 10
): Promise<IOrgClientAuditListResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/clients/audit?page=${page}&size=${size}&sort=createdAt,DESC`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/**
 * Staff view of the orgs' requests (the "setting requests" tab), newest first. `type` and `status`
 * filter; leave them out for every request. Every staff role may look.
 */
export const getOrganizationRequests = (
  status?: OrganizationRequestStatus,
  page = 0,
  size = 10,
  type?: OrganizationRequestType
): Promise<IOrganizationRequestListResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/requests?page=${page}&size=${size}${
    status ? `&status=${status}` : ""
  }${type ? `&type=${type}` : ""}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/** How many requests are waiting (of one type, or of all), for the tab badge. */
export const getOrganizationRequestsCount = (
  type?: OrganizationRequestType
): Promise<IApiResponse<number>> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/requests/count${
    type ? `?type=${type}` : ""
  }`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/**
 * Approves an open request through its type's handler. `reason` is required. The response data is
 * what the handler reports: for `AUTO_CONSENT` `{ approvedLinks }`, the customers that were
 * approved with the client's switch.
 */
export const approveOrganizationRequest = (
  id: number,
  reason: string
): Promise<IApiResponse<{ approvedLinks?: number }>> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/requests/${id}/approve`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, { reason });
};

/** The backend's message from a rejected request (`{ error: { message } }`), if it sent one. */
export const getRequestErrorMessage = (error: unknown): string | undefined => {
  const message = (error as { error?: { message?: unknown } } | null)?.error
    ?.message;
  return typeof message === "string" && message ? message : undefined;
};

/** Rejects an open request; `reason` is required and is shown to the org. */
export const rejectOrganizationRequest = (
  id: number,
  reason: string
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/requests/${id}/reject`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, { reason });
};
